import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import axios from 'axios';
import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import { NovaSectionLoader, NovaButtonLoader } from './ui/NovaLoader';
import '../CSS/Utilisateurs.css';

const emptyForm = {
  nom: '',
  prenom: '',
  email: '',
  role: 'USER',
};

const Utilisateurs = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resendingInvitationId, setResendingInvitationId] = useState(null);
  const [togglingActivationId, setTogglingActivationId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');

  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [currentUserId, setCurrentUserId] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const token = localStorage.getItem('token');

  const authHeaders = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    try {
      if (token) {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(decoded.user_id);
      }
    } catch {
      setCurrentUserId(null);
    }
  }, [token]);

  const normalizeUser = (user) => {
    const compteActif = Number(user.compte_actif ?? 1) === 1 ? 1 : 0;

    return {
      ...user,
      id: user.id,
      nom: user.nom || '',
      prenom: user.prenom || '',
      email: user.email || '',
      role: user.role || user.type || 'USER',
      type: user.type || user.role || 'USER',
      compte_actif: compteActif,
      compte_status: compteActif === 0 ? 'Compte désactivé' : (user.compte_status || 'A compléter'),
    };
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/users`, authHeaders);
      setUsers((response.data || []).map(normalizeUser));
    } catch (error) {
      console.error(error);
      setNotification({
        open: true,
        message: 'Erreur lors du chargement des ressources',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) {
      return users;
    }

    return users.filter((user) => {
      return [
        user.nom,
        user.prenom,
        user.email,
        user.role,
        user.compte_status,
        user.compte_actif === 1 ? 'actif' : 'désactivé',
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [searchTerm, users]);

  const stats = useMemo(() => {
    const total = users.length;
    const admins = users.filter((user) => (user.role || user.type) === 'ADMIN').length;
    const actifs = users.filter((user) => user.compte_status === 'Compte validé' && user.compte_actif === 1).length;
    const desactives = users.filter((user) => user.compte_actif === 0).length;

    return { total, admins, actifs, desactives };
  }, [users]);

  const getInitials = (user) => {
    const first = (user.prenom || '').charAt(0);
    const last = (user.nom || '').charAt(0);
    return `${first}${last}`.toUpperCase() || 'R';
  };

  const openAddDialog = () => {
    setDialogMode('add');
    setSelectedUser(null);
    setForm(emptyForm);
    setOpenUserDialog(true);
  };

  const openEditDialog = (user) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setForm({
      nom: user.nom || '',
      prenom: user.prenom || '',
      email: user.email || '',
      role: user.role || user.type || 'USER',
    });
    setOpenUserDialog(true);
  };

  const closeUserDialog = () => {
    if (saving) return;
    setOpenUserDialog(false);
    setSelectedUser(null);
    setForm(emptyForm);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
      setNotification({
        open: true,
        message: 'Nom, prénom et email sont obligatoires.',
        severity: 'error',
      });
      return false;
    }

    if (!form.email.includes('@') || !form.email.includes('.')) {
      setNotification({
        open: true,
        message: 'Adresse email invalide.',
        severity: 'error',
      });
      return false;
    }

    return true;
  };

  const handleAddUser = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      await axios.post(
        `${apiUrl}/register`,
        {
          nom: form.nom.trim(),
          prenom: form.prenom.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
        },
        authHeaders
      );

      setNotification({
        open: true,
        message: 'Ressource ajoutée avec succès.',
        severity: 'success',
      });

      closeUserDialog();
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erreur lors de la création';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser || !validateForm()) return;

    try {
      setSaving(true);

      const response = await axios.put(
        `${apiUrl}/users/${selectedUser.id}`,
        {
          nom: form.nom.trim(),
          prenom: form.prenom.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
        },
        authHeaders
      );

      const updatedUser = normalizeUser(response.data.user || {
        ...selectedUser,
        ...form,
        email: form.email.trim().toLowerCase(),
        type: form.role,
        role: form.role,
      });

      setUsers((current) =>
        current.map((user) => String(user.id) === String(selectedUser.id) ? updatedUser : user)
      );

      setNotification({
        open: true,
        message: response.data.message || 'Ressource mise à jour avec succès.',
        severity: 'success',
      });

      closeUserDialog();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erreur lors de la modification';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitUser = () => {
    if (dialogMode === 'add') {
      handleAddUser();
    } else {
      handleEditUser();
    }
  };

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleResendInvitation = async (user) => {
    if (!user?.id) return;

    try {
      setResendingInvitationId(user.id);

      const response = await axios.post(
        `${apiUrl}/users/${user.id}/resend-invitation`,
        {},
        authHeaders
      );

      setNotification({
        open: true,
        message: response.data?.message || 'Invitation renvoyée avec succès.',
        severity: 'success',
      });
    } catch (error) {
      const msg = error.response?.data?.message || "Erreur lors du renvoi de l'invitation.";
      setNotification({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setResendingInvitationId(null);
    }
  };

  const handleToggleActivation = async (user) => {
    if (!user?.id) return;

    const nextActive = user.compte_actif === 1 ? 0 : 1;

    try {
      setTogglingActivationId(user.id);

      const response = await axios.put(
        `${apiUrl}/users/${user.id}/activation`,
        { compte_actif: nextActive },
        authHeaders
      );

      const updatedUser = normalizeUser(response.data.user || {
        ...user,
        compte_actif: nextActive,
        compte_status: nextActive === 1 ? user.compte_status : 'Compte désactivé',
      });

      setUsers((current) =>
        current.map((item) => String(item.id) === String(user.id) ? updatedUser : item)
      );

      setNotification({
        open: true,
        message: response.data?.message || (nextActive === 1 ? 'Compte activé.' : 'Compte désactivé.'),
        severity: 'success',
      });
    } catch (error) {
      const msg = error.response?.data?.message || "Erreur lors du changement d'état du compte.";
      setNotification({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setTogglingActivationId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(
        `${apiUrl}/delete-user?email=${encodeURIComponent(userToDelete.email)}`,
        authHeaders
      );

      setUsers((current) => current.filter((user) => String(user.id) !== String(userToDelete.id)));

      setNotification({
        open: true,
        message: 'Ressource supprimée avec succès.',
        severity: 'success',
      });
    } catch (error) {
      const msg = error.response?.data?.message || 'Erreur lors de la suppression';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const isEditingSelf = selectedUser && String(selectedUser.id) === String(currentUserId);

  return (
    <LayoutDashboard>
      <main className="ressources-page">
        <section className="ressources-hero">
          <div>
            <span className="ressources-pill">Administration</span>
            <h1>Ressources</h1>
            <p>
              Gérez les utilisateurs, leurs informations de contact, leurs accès
              et leurs rôles depuis un espace clair et sécurisé.
            </p>
          </div>

          <Button
            variant="contained"
            className="ressources-add-btn"
            startIcon={<AddRoundedIcon />}
            onClick={openAddDialog}
          >
            Ajouter une ressource
          </Button>
        </section>

        <section className="ressources-stats">
          <article>
            <span>Total ressources</span>
            <strong>{stats.total}</strong>
          </article>

          <article>
            <span>Administrateurs</span>
            <strong>{stats.admins}</strong>
          </article>

          <article>
            <span>Comptes validés</span>
            <strong>{stats.actifs}</strong>
          </article>

            <article className="ressources-stat-disabled">
              <span>Comptes désactivés</span>
              <strong>{stats.desactives}</strong>
            </article>
        </section>

        <section className="ressources-panel">
          <div className="ressources-panel-header">
            <div>
              <h2>Liste des ressources</h2>
              <p>Modifiez le nom, le prénom, l’email ou le rôle d’un utilisateur.</p>
            </div>

            <TextField
              className="ressources-search"
              size="small"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: <SearchRoundedIcon className="ressources-search-icon" />,
              }}
            />
          </div>

          <TableContainer className="ressources-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ressource</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 0 }}>
                      <NovaSectionLoader
                        label="Chargement des ressources"
                        helper="Récupération des utilisateurs en cours."
                      />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      Aucune ressource trouvée.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelf = String(user.id) === String(currentUserId);
                    const userRole = user.role || user.type || 'USER';

                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="ressources-person">
                            <div className="ressources-avatar">
                              {getInitials(user)}
                            </div>

                            <div>
                              <strong>{user.prenom} {user.nom}</strong>
                              {isSelf && <span>Vous</span>}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <span className="ressources-email">{user.email}</span>
                        </TableCell>

                        <TableCell>
                          <span className={userRole === 'ADMIN' ? 'role-chip admin' : 'role-chip user'}>
                            {userRole}
                          </span>
                        </TableCell>

                        <TableCell>
                          <span className={
                            user.compte_actif === 0
                              ? 'status-chip disabled'
                              : user.compte_status === 'Compte validé'
                                ? 'status-chip ok'
                                : 'status-chip pending'
                          }>
                            {user.compte_status}
                          </span>
                        </TableCell>

                        <TableCell align="right">
                          <div className="ressources-actions">
                            <Tooltip title="Modifier" arrow>
                              <IconButton
                                className="edit-action"
                                onClick={() => openEditDialog(user)}
                              >
                                <EditRoundedIcon />
                              </IconButton>
                            </Tooltip>

                            {user.compte_status !== 'Compte validé' && (
                              <Tooltip title="Renvoyer l’invitation" arrow>
                                <span>
                                  <IconButton
                                    className="resend-action"
                                    onClick={() => handleResendInvitation(user)}
                                    disabled={resendingInvitationId === user.id}
                                  >
                                    {resendingInvitationId === user.id ? (
                                      <NovaButtonLoader label="Renvoi en cours" />
                                    ) : (
                                      <MarkEmailUnreadRoundedIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            <Tooltip
                              title={
                                isSelf
                                  ? 'Vous ne pouvez pas désactiver votre propre compte'
                                  : user.compte_actif === 1
                                    ? 'Désactiver le compte'
                                    : 'Activer le compte'
                              }
                              arrow
                            >
                              <span>
                                <IconButton
                                  className={
                                    user.compte_actif === 1
                                      ? 'activation-action deactivate'
                                      : 'activation-action activate'
                                  }
                                  onClick={() => handleToggleActivation(user)}
                                  disabled={isSelf || togglingActivationId === user.id}
                                >
                                  {togglingActivationId === user.id ? (
                                    <NovaButtonLoader label="Changement en cours" />
                                  ) : (
                                    <PowerSettingsNewRoundedIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip
                              title={isSelf ? 'Vous ne pouvez pas vous supprimer vous-même' : 'Supprimer'}
                              arrow
                            >
                              <span>
                                <IconButton
                                  className="delete-action"
                                  onClick={() => handleOpenDeleteDialog(user)}
                                  disabled={isSelf}
                                >
                                  <DeleteRoundedIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </section>
      </main>

      <Dialog
        open={openUserDialog}
        onClose={closeUserDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: 'ressources-dialog' }}
      >
        <DialogTitle>
          {dialogMode === 'add' ? 'Ajouter une ressource' : 'Modifier la ressource'}
        </DialogTitle>

        <DialogContent>
          <div className="ressources-form-grid">
            <TextField
              label="Nom"
              fullWidth
              value={form.nom}
              onChange={(event) => updateForm('nom', event.target.value)}
            />

            <TextField
              label="Prénom"
              fullWidth
              value={form.prenom}
              onChange={(event) => updateForm('prenom', event.target.value)}
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              value={form.email}
              onChange={(event) => updateForm('email', event.target.value)}
            />

            <TextField
              select
              label="Rôle"
              fullWidth
              value={form.role}
              disabled={Boolean(isEditingSelf)}
              helperText={isEditingSelf ? 'Vous ne pouvez pas modifier votre propre rôle.' : ''}
              onChange={(event) => updateForm('role', event.target.value)}
            >
              <MenuItem value="USER">USER</MenuItem>
              <MenuItem value="ADMIN">ADMIN</MenuItem>
            </TextField>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeUserDialog} disabled={saving}>
            Annuler
          </Button>

          <Button
            variant="contained"
            className="ressources-save-btn"
            onClick={handleSubmitUser}
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : dialogMode === 'add' ? 'Ajouter' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'ressources-dialog' }}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>

        <DialogContent>
          {userToDelete && (
            <Typography>
              Supprimer <strong>{userToDelete.prenom} {userToDelete.nom}</strong> ?
              <br />
              <span className="ressources-danger-text">
                Cette action est irréversible.
              </span>
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Annuler
          </Button>

          <Button color="error" variant="contained" onClick={handleDeleteUser}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3500}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} sx={{ width: '100%', fontWeight: 800 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </LayoutDashboard>
  );
};

export default Utilisateurs;
